/**
 * WeeklyTaskCriteriaPage — Workflow · Weekly Task Criteria.
 *
 * Per-category thresholds that drive Tuesday's WeeklyTaskGeneratorService.
 * Five fixed categories (NEWESI / NOFUND / NEWOD / NEWFOL / ZROCLS), one
 * inline card each. Inactive categories short-circuit the generator entirely.
 *
 * Contract (verified 2026-06-16 — V3WeeklyTasksFunction.AdminCriteriaUpsert
 * persists every field; bounded category set enforced server-side):
 *   - Read:  client.retail.tasks.weekly.criteriaList()  → GET /v3/admin/weekly-task-criteria
 *   - Write: client.retail.tasks.weekly.criteriaUpsert(token, body)
 *            → POST /v3/admin/weekly-task-criteria
 *
 * Migrated 2026-06-16 from ROP-teams Admin (operational app writes no config).
 * Auth: ADMIN_AUTH_TOKEN_SENTINEL (MSAL bearer; no secret in the bundle).
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { ADMIN_AUTH_TOKEN_SENTINEL } from '@retail-ops/api-client';
import type { WeeklyTaskCategory, WeeklyTaskCriteria } from '@retail-ops/shared-types';
import { useApiClient } from '@/contexts/ApiClientContext';
import { Banner, Button, Card, Pill, Spinner } from '@/components/primitives';
import { SelectField, NumberField, TextArea } from '@/components/FormField';

const CATEGORY_LABEL: Record<WeeklyTaskCategory, string> = {
  NEWESI: 'NEWESI — New accounts, ESI follow-up',
  NOFUND: 'NOFUND — New accounts, not yet funded',
  NEWOD: 'NEWOD — New DDA, OD protection within next week',
  NEWFOL: 'NEWFOL — New accounts (parent listing)',
  ZROCLS: 'ZROCLS — DDA at $0 for 3+ weeks',
};

const ORDER: ReadonlyArray<WeeklyTaskCategory> = ['NEWESI', 'NOFUND', 'NEWOD', 'NEWFOL', 'ZROCLS'];

const ACCOUNT_TYPE_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'ALL', label: 'ALL' },
  { value: 'DDA', label: 'DDA' },
  { value: 'SAV', label: 'SAV' },
];

const ACTIVE_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: '1', label: 'Yes — generator runs this category' },
  { value: '0', label: 'No — short-circuit (no rows generated)' },
];

interface DraftRow extends WeeklyTaskCriteria {
  dirty: boolean;
}

export function WeeklyTaskCriteriaPage(): JSX.Element {
  const client = useApiClient();
  const [rows, setRows] = useState<ReadonlyArray<DraftRow> | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [savingFor, setSavingFor] = useState<WeeklyTaskCategory | null>(null);
  const [toast, setToast] = useState<{ tone: 'info' | 'warn'; text: string } | null>(null);
  const [refreshNonce, setRefreshNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setRows(null);
      setLoadError(null);
      try {
        const resp = await client.retail.tasks.weekly.criteriaList();
        if (cancelled) return;
        setRows((resp.criteria ?? []).map((c) => ({ ...c, dirty: false })));
      } catch (e) {
        if (cancelled) return;
        setLoadError(e instanceof Error ? e.message : 'Failed to load criteria.');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [client, refreshNonce]);

  const refresh = useCallback(() => setRefreshNonce((n) => n + 1), []);

  const update = useCallback((cat: WeeklyTaskCategory, patch: Partial<WeeklyTaskCriteria>) => {
    setRows((prev) =>
      prev ? prev.map((r) => (r.taskCategory === cat ? { ...r, ...patch, dirty: true } : r)) : prev,
    );
  }, []);

  const save = useCallback(
    async (cat: WeeklyTaskCategory) => {
      const row = rows?.find((r) => r.taskCategory === cat);
      if (!row) return;
      setSavingFor(cat);
      try {
        const resp = await client.retail.tasks.weekly.criteriaUpsert(ADMIN_AUTH_TOKEN_SENTINEL, {
          taskCategory: cat,
          isActive: row.isActive,
          ageThresholdDays: row.ageThresholdDays,
          balanceThresholdAmount: row.balanceThresholdAmount,
          accountTypeFilter: row.accountTypeFilter,
          notes: row.notes,
          updatedBy: 'admin-panel',
        });
        if (!resp.success) {
          setToast({ tone: 'warn', text: resp.error ?? 'Save failed.' });
          return;
        }
        setToast({ tone: 'info', text: `Saved ${cat}.` });
        refresh();
      } catch (e) {
        setToast({ tone: 'warn', text: e instanceof Error ? e.message : 'Save failed.' });
      } finally {
        setSavingFor(null);
      }
    },
    [client, rows, refresh],
  );

  const ordered = useMemo<ReadonlyArray<DraftRow>>(
    () => ORDER.map((cat) => (rows ?? []).find((r) => r.taskCategory === cat)).filter((r): r is DraftRow => Boolean(r)),
    [rows],
  );

  if (loadError) {
    return (
      <Banner tone="err">
        Could not load weekly-task criteria: {loadError} <Button onClick={refresh}>Retry</Button>
      </Banner>
    );
  }
  if (!rows) return <Spinner label="Loading weekly-task criteria…" />;

  return (
    <>
      {toast && (
        <Banner tone={toast.tone}>
          {toast.text} <Button onClick={() => setToast(null)}>Dismiss</Button>
        </Banner>
      )}

      <Banner tone="info">
        Tune per-category thresholds that drive Tuesday's weekly-task generation. Five rows, one per
        category. Inactive categories short-circuit the generator entirely.
      </Banner>

      {ordered.length === 0 && (
        <Banner tone="warn">
          No criteria rows present. Seed <code className="cell-mono">25-weekly-task-criteria.sql</code> first.
        </Banner>
      )}

      {ordered.map((row) => (
        <Card
          key={row.taskCategory}
          title={CATEGORY_LABEL[row.taskCategory]}
          actions={
            <div className="card-actions-row">
              <Pill tone={row.isActive ? 'ok' : 'muted'}>{row.isActive ? 'Active' : 'Inactive'}</Pill>
              {row.dirty && <Pill tone="warn">Unsaved</Pill>}
            </div>
          }
        >
          <div className="filter-grid">
            <SelectField
              label="Active"
              value={row.isActive ? '1' : '0'}
              onChange={(v) => update(row.taskCategory, { isActive: v === '1' })}
              options={ACTIVE_OPTIONS}
            />
            <SelectField
              label="Account type filter"
              value={row.accountTypeFilter ?? 'ALL'}
              onChange={(v) => update(row.taskCategory, { accountTypeFilter: v })}
              options={ACCOUNT_TYPE_OPTIONS}
            />
            <NumberField
              label="Age threshold (days)"
              value={row.ageThresholdDays ?? null}
              onChange={(v) => update(row.taskCategory, { ageThresholdDays: v })}
              min={0}
            />
            <NumberField
              label="Balance threshold ($)"
              value={row.balanceThresholdAmount ?? null}
              onChange={(v) => update(row.taskCategory, { balanceThresholdAmount: v })}
              min={0}
              step={0.01}
            />
          </div>
          <TextArea
            label="Notes (admin-facing)"
            value={row.notes ?? ''}
            onChange={(v) => update(row.taskCategory, { notes: v })}
            rows={2}
          />
          <div className="card-actions-row filter-actions">
            <Button
              variant="primary"
              onClick={() => void save(row.taskCategory)}
              disabled={!row.dirty || savingFor === row.taskCategory}
            >
              {savingFor === row.taskCategory ? 'Saving…' : 'Save changes'}
            </Button>
          </div>
        </Card>
      ))}
    </>
  );
}
